// Type-only, and a namespace import so the `Sentry.Event` usage sites below read
// unchanged. `import type` guarantees the whole thing is erased, which matters:
// this module is pulled into the client bundle by `sentry.client.config.ts`, and
// a value import here would resolve through the `@/observability/sentry` alias
// and pull a second copy of the SDK in beside the one `init` already loads.
import type * as Sentry from "@/observability/sentry";
import { AxiosError, isAxiosError } from "axios";

// rrweb method names that survive minification — used to detect posthog
// recorder errors from cross-origin iframe access after lazy-recorder was
// bundled and the URL-based denyUrls stopped matching.
const POSTHOG_RECORDER_FRAME_FUNCTIONS =
  /(?:^|\.)(?:patchAttachShadow|observeAttachShadow|onIframeLoad|attachIframe|recordCanvas)\b/;

function isPosthogRecorderError(event: Sentry.Event | undefined): boolean {
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames) return false;
  return frames.some(
    (f) =>
      typeof f.function === "string" &&
      POSTHOG_RECORDER_FRAME_FUNCTIONS.test(f.function)
  );
}

// Where the build emits our own chunks. Same-origin frames from anywhere else
// are injected script, so this string decides whether an error is ours — it was
// `/_next/static/` under Next and must track Vite's `build.assetsDir`.
const OWN_CHUNK_PATH = "/assets/";

// Third-party widget bundle chunk names that never appear in our own build.
// The Intercom messenger ships chunks like `frame-modern.<hash>.js` and
// `app~tooltips-modern.<hash>.js`; `widget-v2` is another injected widget. When
// these throw from a wrapped DOM event listener (e.g. a `mousemove` handler),
// Sentry's instrumentation wrapper (`helpers.js`) sits at `frames[0]`, so the
// widget chunk is buried mid-stack — hence we scan every frame, not just the first.
const THIRD_PARTY_WIDGET_CHUNKS = [
  "frame-modern",
  "tooltips-modern",
  "widget-v2",
];

// disable errors originating from injected scripts such as Google Tag Manager
function isInjectedCode(event: Sentry.Event | undefined): boolean {
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames;

  if (!frames || frames.length === 0) return false;

  if (
    frames.some(
      (frame) =>
        typeof frame.filename === "string" &&
        THIRD_PARTY_WIDGET_CHUNKS.some((chunk) =>
          frame.filename!.includes(chunk)
        )
    )
  ) {
    return true;
  }

  const firstFrame = frames[0];
  if (
    firstFrame.filename?.includes("<anonymous>") ||
    firstFrame.filename?.includes("<unknown>")
  ) {
    return true;
  }

  const lastFrame = frames[frames.length - 1];
  if (
    typeof lastFrame.filename === "string" &&
    (lastFrame.filename === window.location.pathname ||
      (lastFrame.filename.startsWith(window.location.origin) &&
        !lastFrame.filename.includes(OWN_CHUNK_PATH)))
  ) {
    return true;
  }

  if (
    frames.some(
      (frame) =>
        typeof frame.filename === "string" &&
        (frame.filename.startsWith("https://www.googletagmanager.com") ||
          frame.filename.startsWith("https://googleads."))
    )
  ) {
    return true;
  }

  return false;
}

const SENSITIVE_KEYS = /password|token|auth|secret|credit_card|cc|card_number/i;

function sanitizeJsonString(data: string): string {
  try {
    const parsed = JSON.parse(data);
    if (parsed && typeof parsed === "object") {
      return JSON.stringify(sanitize(parsed));
    }
  } catch {
    // not JSON
  }
  return data;
}

function sanitizeObject(data: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key in data) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    result[key] = SENSITIVE_KEYS.test(key) ? "[REDACTED]" : sanitize(data[key]);
  }
  return result;
}

function sanitize(data: any): any {
  if (!data) return data;
  if (typeof data === "string") return sanitizeJsonString(data);
  if (Array.isArray(data)) return data.map(sanitize);
  if (typeof data === "object") return sanitizeObject(data);
  return data;
}

// Recursively enhance axios errors with response and request data
// Source idea: https://github.com/getsentry/sentry-javascript/issues/4377#issuecomment-2017757609
function addAxiosContextRecursive(event: Sentry.ErrorEvent, error: unknown) {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
}

function addAxiosContext(event: Sentry.ErrorEvent, error: AxiosError) {
  event.contexts = {
    ...event.contexts,
    axios: {
      request: {
        url: error.config?.url,
        data: sanitize(error.config?.data),
        headers: sanitize(error.config?.headers),
      },
      response: {
        data: sanitize(error.response?.data),
        headers: sanitize(error.response?.headers),
        status: error.response?.status,
      },
    },
  };
}

// Detect minified error messages with no useful stacktrace (e.g. "Sd", "bb", "gb").
// These are unhandled promise rejections from bundled third-party code (likely PostHog)
// on iOS/WebKit where the error message is a terser-mangled variable name and the
// stack is stripped. The minified name changes every build, so matching specific
// strings in ignoreErrors is unsustainable.
const MINIFIED_IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]{0,3}$/;

function isMinifiedErrorWithoutStacktrace(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;

  const message = exception.value;
  if (!message || !MINIFIED_IDENTIFIER.test(message)) return false;

  const frames = exception.stacktrace?.frames;
  if (!frames || frames.length === 0) return true;

  // If all frames are native code (e.g. promiseReactionJob), also filter
  return frames.every(
    (f) => !f.filename || f.filename.includes("[native code]")
  );
}

export default function sentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
) {
  // Don't report injected code errors such as Google Tag Manager
  if (isInjectedCode(event)) {
    return null;
  }

  // Don't report posthog-js recorder errors from cross-origin iframe access
  if (isPosthogRecorderError(event)) {
    return null;
  }

  // Don't report minified errors with no stacktrace (iOS/WebKit third-party noise)
  if (isMinifiedErrorWithoutStacktrace(event)) {
    return null;
  }

  // Enhance axios errors with response and request data
  addAxiosContextRecursive(event, hint?.originalException);

  return event;
}
